import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";

export type WellnessRow = {
  date: string;
  hrv: number | null;
  rhr: number | null;
  sleep_hours: number | null;
  sleep_score: number | null;
  body_weight: number | null;
  readiness: number | null;
  fitness?: number | null;
  fatigue?: number | null;
  form?: number | null;
  hrv_color?: string | null;
  rhr_color?: string | null;
  sleep_color?: string | null;
};

export type WellnessPatch = Partial<{
  hrv: number | null;
  rhr: number | null;
  sleep_hours: number | null;
  sleep_score: number | null;
  body_weight: number | null;
  readiness: number | null;
}>;

export function useWellnessList(daysOrRange: number | { from: string; to: string } = 14) {
  const isRange = typeof daysOrRange === "object";
  const params: Record<string, string | number> = isRange
    ? { from_date: daysOrRange.from, to_date: daysOrRange.to }
    : { days: daysOrRange };
  const key = isRange
    ? ["athlete-wellness", "list", "range", daysOrRange.from, daysOrRange.to]
    : ["athlete-wellness", "list", daysOrRange];
  return useQuery({
    queryKey: key,
    queryFn: async () => {
      const r = await api.get<WellnessRow[]>("/athlete/wellness", { params });
      return r.data;
    },
  });
}

export function useWellnessUpsert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ date, patch }: { date: string; patch: WellnessPatch }) => {
      const r = await api.patch<WellnessRow>(`/athlete/wellness/${date}`, patch);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["athlete-wellness"] });
      qc.invalidateQueries({ queryKey: ["athlete-analytics", "wellness"] });
    },
  });
}

// ─── Sub-drill entries (PH3-3 backed by athlete_wellness_sub) ────────────────

export type WellnessSubValues = Record<string, string | number | Record<string, boolean>>;

export type WellnessSubRow = {
  topic: string;
  date: string;
  values: WellnessSubValues;
  updated_at: string | null;
};

export function useWellnessSubEntry(topic: string, date?: string) {
  return useQuery<WellnessSubRow>({
    queryKey: ["athlete-wellness", "sub", topic, date ?? "today"],
    queryFn: async () => {
      const r = await api.get<WellnessSubRow>(`/athlete/wellness/sub/${topic}`, {
        params: date ? { date } : undefined,
      });
      return r.data;
    },
    staleTime: 30_000,
    enabled: !!topic,
  });
}

export function useWellnessSubRecent(topic: string, days: number = 7) {
  return useQuery<WellnessSubRow[]>({
    queryKey: ["athlete-wellness", "sub", topic, "recent", days],
    queryFn: async () => {
      const r = await api.get<WellnessSubRow[]>(
        `/athlete/wellness/sub/${topic}/recent`,
        { params: { days } },
      );
      return r.data;
    },
    staleTime: 30_000,
    enabled: !!topic,
  });
}

export function useWellnessSubPatch() {
  const qc = useQueryClient();
  return useMutation<
    WellnessSubRow,
    unknown,
    { topic: string; date?: string; values: WellnessSubValues }
  >({
    mutationFn: async ({ topic, date, values }) => {
      const r = await api.patch<WellnessSubRow>(
        `/athlete/wellness/sub/${topic}`,
        { values },
        { params: date ? { date } : undefined },
      );
      return r.data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["athlete-wellness", "sub", vars.topic] });
    },
  });
}

/**
 * Последняя известная готовность с датой — для «Формы».
 *
 * Отдельно от dashboard.readiness_score: тот отвечает строго за сегодня и
 * молчит до утренней синхронизации, что правильно для экрана решения. «Форма»
 * показывает линию, и вчерашняя точка на ней законна — но только с явной
 * датой, иначе выйдет подстановка старого под видом свежего.
 */
export type LatestReadiness = {
  score: number | null;
  color: string;
  source: string;
  on_date: string | null;
  is_today: boolean;
  days_ago: number | null;
};

export function useLatestReadiness() {
  return useQuery({
    queryKey: ["athlete-wellness", "readiness-latest"],
    queryFn: async () => {
      const r = await api.get<LatestReadiness>("/athlete/wellness/readiness/latest");
      return r.data;
    },
  });
}
